import { type Tensor } from 'onnxruntime-web'
import type { BaseImageInfo, ModelInfo } from './image'
import type { Model, RefImage, SettingImage, ModelImage, ModelClothe } from './setting'
import type { ImageClarity } from '@/constants/image'

export type BaProcessorStatus = '' | 'pending' | 'ready'

export type BaRgba = [r: number, g: number, b: number, a: number]

export type BaPoint = [x: number, y: number]

/**
 * 微调选区操作类型
 */
export enum BaClickType {
  /**
   * 添加选区
   */
  add = 1,
  /**
   * 减少选区
   */
  remove = 0,
  upperLeft = 2,
  bottomRight = 3,
  /**
   * 缩放和拖动
   */
  resize = 4,
  /**
   * 无操作
   */
  nop
}

/**
 * 微调图片操作类型，无、鼠标放置、点击
 */
export type BaMaskType = 'none' | 'hover' | 'click'

export interface BaImageProcessorOptions {
  maskColor?: BaRgba
}

export interface BaClickInput {
  x: number
  y: number
  xPercent: number
  yPercent: number
  clickType: number
}

/**
 * 选区面积数据，onnxruntime需要用
 */
export interface OnnxModelScale {
  onnxScale: number
  // maskWidth: number;
  // maskHeight: number;
  scale: number
  // uploadScale: number;
  width: number
  height: number
  scaleWidth: number
  scaleHeight: number
}

export interface OnnxModelData {
  clicks?: Array<BaClickInput>
  tensor: Tensor
  modelScale: OnnxModelScale
  prevPredMask?: Tensor
}

/**
 * 创意板块生成图片入参实体
 */
export interface BaInpaintParams {
  /**
   * 项目ID
   */
  projectId: number
  /**
   * 提示词
   */
  prompt?: string
  /**
   * 自定义模特提示词
   */
  customModelPrompt?: string
  /**
   * 反向提示词
   */
  negativePrompt?: string
  /**
   * 迭代步数
   */
  steps?: number
  /**
   * 重绘幅度
   */
  denoisingStrength?: number
  /**
   * 单次图片数量
   */
  genNum?: number
  /**
   * 品牌模型
   */
  models?: Model[]
  /**
   * 参考图
   */
  refImage?: RefImage[]
  /**
   * 线稿或样衣，暂时只支持上传一张图片，线稿和样衣互斥
   */
  images?: SettingImage[]
  /**
   * 模特信息
   */
  personModel?: ModelInfo
  /**
   * 是否高清
   */
  hiresFix?: boolean
  /**
   * 临时用的 TODO: 3D
   */
  enable3d?: number
  /**
   * 禁用模特
   */
  disabledModel?: boolean
  /**
   * 图片宽高比，宽
   */
  widthRatio?: number
  /**
   * 图片宽高比，高
   */
  heightRatio?: number
  /**
   * 风格融合的款式图
   */
  image?:
    | string
    | {
        /**
         * 背景图片ID
         */
        image: string
        /**
         * 蒙版ID
         */
        mask?: string
      }
  extendSize?: number
  /**
   * AI扩图的参考强度
   */
  extendStrength?: number
  /**
   * 试衣类型
   */
  type?: string
  /**
   * 模特图
   */
  replacedImage?: ModelImage | null
  /**
   * 单件上身数据
   */
  clothe?: ModelImage | null
  /**
   * 多件上身数据
   */
  clothes?: ModelClothe | null
  /**
   * 模特换脸模特id
   */
  modelId?: string | null
  /**
   * 蒙版 ID
   */
  mask?: string
  /**
   * 场景
   */
  scene?: {
    type: string
    name?: string
  }
}

/**
 * 高清入参实体
 */
export interface BaInpaintParamsUpscale extends BaInpaintParams {
  /**
   * 资源 ID
   */
  resourceId: string
  /**
   * 高清倍数
   */
  scaleBy: ImageClarity
}

/**
 * 一键抠图（蒙版）入参实体
 */
export interface BaInpaintParamsMatte extends BaInpaintParams {
  /**
   * 资源 ID
   */
  resourceId: string
}

/**
 * 智能换色入参实体
 */
export interface BaInpaintParamsRecolor extends BaInpaintParams {
  /**
   * 颜色
   */
  color: string
  /**
   * 抠图后的图片 ID
   */
  matteImage: string
}

/**
 * 微调板块生成图片入参实体
 */
export interface BaInpaintParamsTune extends BaInpaintParams {
  /**
   * 底图ID
   */
  image: string
  /**
   * 选区图ID
   */
  mask?: string
}

/**
 * photo板块生成图片入参实体-高级模式
 */
export interface BaInpaintParamsPhoto extends BaInpaintParams {
  /**
   * 选区图ID
   */
  mask: string
  /**
   * 通过蒙版抠出来的图片id
   */
  mattedImage: string
  /**
   * photo模块合成后的图片id
   */
  image: string
  /**
   * 是否有背景图片
   */
  customBackground: number
  /**
   * 是否补光
   */
  light?: boolean
}

/**
 * photo板块生成图片入参实体-简单模式
 */
export interface BaInpaintParamsPhotoEasy extends BaInpaintParams {
  /**
   * 用户上传的前景图片ID
   */
  image: string
  /**
   * 蒙版图片ID
   */
  mask?: string
  /**
   * 背景图片ID
   */
  background?: string
  /**
   * 背景权重
   */
  weight?: number
  /**
   * 是否补光
   */
  light?: boolean
}

/**
 * 模特试穿板块生成图片入参实体
 */
export interface BaInpaintParamsModelTryOn extends BaInpaintParams {
  /**
   * 项目id
   */
  projectId: number
  /**
   * 生成张数
   */
  genNum: number
  /**
   * 模特图
   */
  replacedImage: ModelImage | null
  /**
   * 单件上身数据
   */
  clothe?: ModelImage | null
  /**
   * 多件上身数据
   */
  clothes?: ModelClothe | null
  /**
   * 单件上身还是多件上身
   */
  type: string
}

/**
 * 模特换脸板块生成图片入参实体
 */
export interface BaInpaintParamsModelFace extends BaInpaintParams {
  /**
   * 项目id
   */
  projectId: number
  /**
   * 生成张数
   */
  genNum: number
  /**
   * 模特换脸图片
   */
  replacedImage: ModelImage | null
  /**
   * 模特换脸模特id
   */
  modelId: string | null
  /**
   * 头身比
   */
  faceSize: number
  /**
   * 提示词
   */
  prompt?: string
}

/**
 * 花纹扩展板块生成图片入参实体
 */
export interface BaInpaintParamsPattern extends BaInpaintParams {
  /**
   * 合成图片ID
   */
  patternImage: string
  /**
   * 背景图片和蒙版图片
   */
  image: {
    /**
     * 背景图片ID
     */
    image: string
    /**
     * 蒙版ID
     */
    mask?: string
  }
}

/**
 * 花型提取板块生成图片入参实体
 */
export interface BaInpaintParamsPatternExtraction extends BaInpaintParams {
  /**
   * 底图ID
   */
  patternImage: string
  /**
   * 生成张数
   */
  genNum: number
  /**
   * 提示词
   */
  prompt?: string
  /**
   * 类型
   */
  kind: number
}

/**
 * 服装提取板块生成图片入参实体
 */
export interface BaInpaintParamsClothingExtraction extends BaInpaintParams {
  /**
   * 底图ID
   */
  image: string
  /**
   * 生成张数
   */
  genNum: number
  /**
   * 提示词
   */
  prompt?: string
  /**
   * 服装类型
   */
  clothes_type?: string
}

/**
 * 服装提取板块生成图片入参实体
 */
export interface BaInpaintParamsStylePicture extends BaInpaintParams {
  /**
   * 底图ID
   */
  image: string
  /**
   * 生成张数
   */
  genNum: number
}

/**
 * 材质转换板块生成图片入参实体
 */
export interface BaInpaintParamsMaterialConversion extends BaInpaintParams {
  /**
   * 底图ID
   */
  patternImage: string
  /**
   * 生成张数
   */
  genNum: number
  /**
   * 提示词
   */
  prompt?: string
  /**
   * 参考图
   */
  reference_image: string
}

/**
 * 款式图加印花板块生成图片入参实体
 */
export interface BaInpaintParamsStyleImagePrinting extends BaInpaintParams {
  /**
   * 印花图ID
   */
  patten_image: string
}

/**
 * 生成图片任务图片状态信息
 * 款式图板块生成图片入参实体
 */
export interface BaInpaintParamsStylePicture extends BaInpaintParams {
  /**
   * 底图ID
   */
  image: string
  /**
   * 生成张数
   */
  genNum: number
  /**
   * 服装类型
   */
  clothes_type: string
}

/**
 * 款式图加面料板块生成图片入参实体
 */
export interface BaInpaintParamsFlatSketchFabric extends BaInpaintParams {
  /**
   * 面料图ID
   */
  fabric_image: string
}
/* 
生成图片任务图片状态信息
*/
export interface BaInpaintImage {
  /**
   * project item id
   */
  id: number
  /**
   * 任务ID
   */
  taskId: string
  /**
   * 处理状态
   */
  status: BaInpaintTaskStatus
  /**
   * 图片基本信息
   */
  image: BaseImageInfo
}

/**
 * 图片语义分割及生成图片任务状态
 */
export enum BaInpaintTaskStatus {
  /**
   * 等待中-入库
   */
  inQueue = 0,
  /**
   * 等待中-入队列
   */
  inWaiting = 5,
  /**
   * 处理中
   */
  inProgress = 1,
  /**
   * 处理完成
   */
  completed = 2,
  /**
   * 处理失败
   */
  failed = 3,
  /**
   * 审核不通过
   */
  notVerify = 4
}
